import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { buttonClasses } from '@/design-system/Button'

import { Badge, Card, Overline, Section, SectionTitle } from '@/design-system/primitives'
import { IncludedList } from '@/design-system/IncludedList'
import { QuantityStepper } from '@/features/tickets/components'
import { formatMoney } from '@/lib/money'

import { usePublicEvent } from '@/features/event/api'
import { readBooleanSetting } from '@/features/event/types'

import { usePublicMenu, type PublicFoodItem, type PublicFoodPack, type PublicMenu } from './api'

function Price({ minor, currency, locale }: { minor: number; currency: string; locale: string }) {
  return (
    <span className="shrink-0 tabular-nums text-champagne-300">
      {formatMoney({ amount_minor: minor, currency }, locale)}
    </span>
  )
}

function AvailabilityBadge({ value }: { value: PublicFoodItem['availability'] }) {
  const { t } = useTranslation()
  if (value === 'sold_out') return <Badge tone="neutral">{t('menu.soldOut')}</Badge>
  if (value === 'unavailable') return <Badge tone="neutral">{t('menu.unavailable')}</Badge>
  if (value === 'low') return <Badge tone="ember">{t('menu.low')}</Badge>
  return null
}

/** La carte, sur la page d'accueil. Lecture seule : l'achat se fait dans le tunnel. */
export function MenuSection({ menu, locale }: { menu: PublicMenu; locale: string }) {
  const { t } = useTranslation()
  if (menu.items.length === 0 && menu.packs.length === 0) return null

  const byCategory = menu.categories
    .map((category) => ({
      category,
      items: menu.items.filter((item) => item.category_id === category.id),
    }))
    .filter((group) => group.items.length > 0)

  const uncategorised = menu.items.filter((item) => !item.category_id)

  return (
    <Section id="food">
      <Overline>Food</Overline>
      <SectionTitle>{t('menu.title')}</SectionTitle>

      {menu.packs.length > 0 ? (
        <div className="mt-6">
          <p className="overline text-content-muted">{t('menu.packs')}</p>
          <ul className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {menu.packs.map((pack) => (
              <li key={pack.id}>
                <Card interactive className="h-full">
                  <div className="flex items-baseline justify-between gap-4">
                    <p className="font-display text-heading-md text-content-primary">
                      {pack.name}
                    </p>
                    <Price minor={pack.price_minor} currency={pack.currency} locale={locale} />
                  </div>
                  {pack.description ? (
                    <p className="mt-2 text-sm text-content-secondary">{pack.description}</p>
                  ) : null}
                  <IncludedList
                    label={t('menu.contains')}
                    items={pack.contents}
                    className="mt-3"
                  />
                  <div className="mt-4">
                    <AvailabilityBadge value={pack.availability} />
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {[...byCategory, ...(uncategorised.length ? [{ category: null, items: uncategorised }] : [])].map(
        (group) => (
          <div key={group.category?.id ?? 'autres'} className="mt-6">
            <p className="overline text-content-muted">
              {group.category?.name ?? t('menu.dishes')}
            </p>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {group.items.map((item) => (
                <li
                  key={item.id}
                  className="flex items-start justify-between gap-6 rounded-md border border-transparent px-4 py-3.5 transition duration-[var(--dur-base)] ease-out hover:border-line-subtle hover:bg-bg-elevated"
                >
                  {/* La photo était envoyée par l'API et ignorée ici : celui
                      qui la déposait en back-office ne la voyait jamais
                      apparaître, sans comprendre pourquoi. Vignette carrée
                      pour que trente plats aux photos disparates gardent une
                      grille régulière. */}
                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt=""
                      loading="lazy"
                      className="h-16 w-16 shrink-0 rounded-md object-cover"
                    />
                  ) : null}

                  <div className="min-w-0 flex-1">
                    <p className="text-content-primary">{item.name}</p>
                    {item.description ? (
                      <p className="mt-1 text-sm text-content-secondary">{item.description}</p>
                    ) : null}
                    {item.allergens.length > 0 ? (
                      <p className="mt-1 text-xs text-content-muted">
                        {t('menu.allergens')} : {item.allergens.join(', ')}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <AvailabilityBadge value={item.availability} />
                    <Price
                      minor={item.price_minor}
                      currency={item.currency}
                      locale={locale}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ),
      )}

      {/* Sans ce bouton, la carte était un cul-de-sac : on lisait les plats,
          on les voulait, et rien ne disait où les commander. Le tunnel de
          billetterie porte aussi la nourriture — encore fallait-il y mener. */}
      <div className="mt-8">
        <Link to="/billets?food=1" className={buttonClasses({ size: 'sm' })}>
          {t('menu.order')}
        </Link>
      </div>

      {menu.admission_notice ? (
        <div className="glass mt-6 max-w-2xl rounded-lg px-6 py-5">
          <p className="text-sm text-content-secondary">⚠️ {menu.admission_notice}</p>
        </div>
      ) : null}
    </Section>
  )
}

/** Étape « restauration » du tunnel d'achat. Chaque ligne est quantifiable. */
export function FoodPicker({
  menu,
  quantities,
  onChange,
  locale,
}: {
  menu: PublicMenu
  quantities: Record<string, number>
  onChange: (key: string, value: number) => void
  locale: string
}) {
  const { t } = useTranslation()

  function row(
    key: string,
    entry: PublicFoodItem | PublicFoodPack,
    extra?: string,
  ) {
    // Épuisé ou retiré de la vente : visible, mais rien à ajouter au panier.
    const soldOut = entry.availability === 'sold_out' || entry.availability === 'unavailable'

    return (
      <Card key={key} className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-content-primary">{entry.name}</p>
            {entry.description ? (
              <p className="mt-1 text-sm text-content-secondary">{entry.description}</p>
            ) : null}
            {extra ? <p className="mt-1 text-sm text-content-muted">{extra}</p> : null}
          </div>
          <Price minor={entry.price_minor} currency={entry.currency} locale={locale} />
        </div>

        <div className="flex items-center justify-between gap-4">
          <AvailabilityBadge value={entry.availability} />
          {!soldOut ? (
            <QuantityStepper
              value={quantities[key] ?? 0}
              onChange={(value) => onChange(key, value)}
              max={entry.max_per_order ?? 10}
              label={entry.name}
            />
          ) : null}
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {menu.packs.length > 0 ? (
        <div className="space-y-4">
          <p className="overline text-content-muted">{t('menu.packs')}</p>
          {menu.packs.map((pack) =>
            row(
              `pack:${pack.id}`,
              pack,
              pack.contents.length > 0
                ? `${t('menu.contains')} : ${pack.contents.join(' · ')}`
                : undefined,
            ),
          )}
        </div>
      ) : null}

      {menu.items.length > 0 ? (
        <div className="space-y-4">
          <p className="overline text-content-muted">{t('menu.dishes')}</p>
          {menu.items.map((item) =>
            row(
              `item:${item.id}`,
              item,
              item.allergens.length > 0
                ? `${t('menu.allergens')} : ${item.allergens.join(', ')}`
                : undefined,
            ),
          )}
        </div>
      ) : null}

      {menu.admission_notice ? (
        <p className="rounded-md border border-line-subtle bg-bg-overlay p-4 text-sm text-content-secondary">
          ⚠️ {menu.admission_notice}
        </p>
      ) : null}
    </div>
  )
}

/**
 * « Commander à manger » depuis une commande qui donne l'entrée.
 *
 * En ligne, on ne commande à manger qu'avec une entrée. Celui qui a déjà la
 * sienne arrive ici depuis sa commande : le lien emporte son numéro, et le
 * tunnel n'a plus qu'à lui demander son adresse pour l'y rattacher.
 *
 * Rien ne s'affiche si la vente est fermée ou si la carte est vide : un
 * bouton qui mène à un refus est une fausse promesse.
 */
export function AddFoodLink({ orderNumber }: { orderNumber: string }) {
  const { t } = useTranslation()
  const event = usePublicEvent().data
  const menu = usePublicMenu(event?.slug).data
  if (!event || !readBooleanSetting(event.settings, 'sales.open', false)) return null
  if (!menu || (menu.items.length === 0 && menu.packs.length === 0)) return null
  return (
    <Link
      to={`/billets?food=1&ref=${encodeURIComponent(orderNumber)}`}
      className={buttonClasses({ variant: 'outline', size: 'md' })}
    >
      {t('admission.orderFood')}
    </Link>
  )
}
